'use client';

import { useMemo, useState } from 'react';
import { countLines, repeatText, type RepeatSeparator } from '@/lib/browser-gencalc-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserTextRepeater() {
  const [input, setInput] = useState('Hello');
  const [count, setCount] = useState('5');
  const [separator, setSeparator] = useState<RepeatSeparator>('newline');
  const [customSeparator, setCustomSeparator] = useState(' | ');
  const [numbered, setNumbered] = useState(false);
  const [trim, setTrim] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const parsedCount = Number(count);
  const output = useMemo(
    () =>
      repeatText({
        text: input,
        count: parsedCount,
        separator,
        customSeparator,
        numbered,
        trim,
      }),
    [input, parsedCount, separator, customSeparator, numbered, trim],
  );

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Text</span>
        <h2 className="tool-live-title">Text repeater</h2>
        <p className="tool-live-subtitle">
          Repeat any word, line, or block of text as many times as you need, with your choice of
          separator. Runs entirely in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Text to repeat</span>
              <textarea
                onChange={(event) => setInput(event.target.value)}
                placeholder="Enter text"
                rows={4}
                value={input}
              />
            </label>
            <label className="tool-live-field">
              <span>Repeat count</span>
              <input
                inputMode="numeric"
                onChange={(event) => setCount(event.target.value)}
                placeholder="5"
                type="number"
                value={count}
              />
            </label>
            <label className="tool-live-field">
              <span>Separator</span>
              <select
                onChange={(event) => setSeparator(event.target.value as RepeatSeparator)}
                value={separator}
              >
                <option value="newline">New line</option>
                <option value="space">Space</option>
                <option value="comma">Comma</option>
                <option value="none">None</option>
                <option value="custom">Custom</option>
              </select>
            </label>
            {separator === 'custom' ? (
              <label className="tool-live-field">
                <span>Custom separator</span>
                <input
                  onChange={(event) => setCustomSeparator(event.target.value)}
                  placeholder=" | "
                  type="text"
                  value={customSeparator}
                />
              </label>
            ) : null}
            <label className="tool-live-field">
              <span>Number each line</span>
              <select
                onChange={(event) => setNumbered(event.target.value === 'yes')}
                value={numbered ? 'yes' : 'no'}
              >
                <option value="no">No</option>
                <option value="yes">Yes (1. 2. 3.)</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Trim whitespace</span>
              <select
                onChange={(event) => setTrim(event.target.value === 'yes')}
                value={trim ? 'yes' : 'no'}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Output</span>
            <textarea readOnly rows={10} value={output} />
          </label>
          <p className="tool-live-panel-note">
            {output.length.toLocaleString()} characters, {countLines(output).toLocaleString()}{' '}
            lines. Generated locally in your browser.
          </p>
        </section>
      </div>
    </div>
  );
}
